import React from "react"
import { useNavigate } from "react-router-dom"

import { useAlert, useAuth } from "hooks"
import * as BikeApi from "features/bike/api/BikeApi"

import { getEntityId, getOldStoreId } from "utils/user"
import { normalizeBoolean } from "utils/functions"
import { BIKE_BACK_STORE, BIKE_STOLEN, INTER_CHAIN, operatorList, MFC_OPERATOR, bikeTypes, bikeBrands, ARCADE_STOREID, ARCADE_STOREID_ST } from "utils/constants"

const MF_SUFFIXE_ELECTRIQUE = "ELECTRIQUE"

// We have 2 states for one bike
// initialBike = it's the bike from API. We need to "prepare" it.
// And with initialBike's information, we can set data to bikeStatus, isSeller, showOwner and isWalletSeller
// currentBike = it's the bike displayed on application

const BikeStoreContext = React.createContext({
    initialBike: null,
    currentBike: null,
    bikeStatus: null,

    isSeller: true,
    showOwner: false,
    isWalletSeller: false,

    setInitialBike: () => {},
    setCurrentBike: () => {},
    setShowOwner: () => {},

    fetchBike: (slug) => {},
    resetBikeStoreInfo: () => {},
})

const useBikeStore = () => React.useContext(BikeStoreContext)

/**********************
 ** Context provider **
 **********************/

const BikeStoreProvider = ({ children }) => {
	const [initialBike, setInitialBike] = React.useState(null)
	const [currentBike, setCurrentBike] = React.useState(null)

    const [bikeStatus, setBikeStatus] = React.useState(null)
    const [showOwner, setShowOwner] = React.useState(false)
    // is the user the seller of the given bike ?
    const [isSeller, setIsSeller] = React.useState(true)
    // un wallet seller est un loueur de vélo via le wallet. Il utilise le store pour revendre les vélos qui lui appartiennent (consumerId === walletId)
    const [isWalletSeller, setIsWalletSeller] = React.useState(false)

    const { alert } = useAlert()
    const { entity } = useAuth()
    const { logout } = useAuth()
	const navigate = useNavigate()

    // or is the user an admin ?
    const isBO = entity?.entity_data?.entity_permission?.role === "MANAGEENTITIES"

    const resetBikeStoreInfo = () => {
        setBikeStatus(null)
        setCurrentBike(null)
        setShowOwner(false)
        setIsSeller(true)
        setIsWalletSeller(false)
    }

    const fetchBike = async (slug) => {
        try {
            const { res: getBike, getError } = await BikeApi.getBike(slug, "byBikeId")
            if (getError()) throw getError()
            setInitialBike(getBike)

        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString()
            if (erroMsg) alert("error", erroMsg)

            if (["BikeNotFoundException", "InvalidArgument", "EntityStatusException"].includes(error?.code)) {
                showOwner ? setShowOwner(false) : navigate(-1)
            }
            if (["NotAuthorizedException"].includes(error?.code)) logout()
        }
    }

    const preparedBike = (getBike) => {
        const loadedBikeBasis = {
            code: getBike.bike_id || "",
            operator: getBike.operator || "",
            accountType: getBike.account_type || "",
            identifier: operatorList.find(oneOperator => oneOperator.id === getBike.operator)?.logo || "",
            status: getBike.bike_status || "",
            row_version: getBike.row_version || "",
            ocode_id: getBike.ocode_id || "",
            bike_description_manufacturer_id: getBike.bike_description_manufacturer_id || "",
            descriptionMadeBy: getBike?.description_made_by || "",
            bikeSeller: getBike?.bike_last_registered_by?.social_reason || "",
            bikeOwner: getBike?.bike_owner || null,
            bikeLastRegisteredBy: getBike?.bike_last_registered_by || null,
            propertyTransferRequest: getBike?.property_transfer_request || null,
            lastEvent: getBike?.extra_data?.last_event_ocodepro || null,
            mailVerified: getBike?.mail_verified || false
        }

        const loadedBikeDescription = {
            bikeBrand: getBike?.bike_description?.brand || "",
            bikeBrandId: getBike?.bike_description?.brand_id || "",
            bikeModel: getBike?.bike_description?.model || "",
            bikeTypeId: getBike?.bike_description?.gear_type_id || "",
            bikeColor: getBike?.bike_description?.color || "",
            bikeSerial: getBike?.bike_description?.frame_serial_number || "",
            ...(() => {
                let tabGearType = getBike?.bike_description?.gear_type?.split(" ") || ""
                let isVAE = ""

                if (getBike?.bike_description) {
                    isVAE = getBike?.bike_description?.is_electric ? String(getBike?.bike_description?.is_electric) : "false"
                    if (getBike?.bike_description?.is_electric) {
                        isVAE = String(getBike?.bike_description?.is_electric)
                    } else if (tabGearType) {
                        // we check if electric when back to store from IN
                        isVAE = String(tabGearType[tabGearType.length - 1] === MF_SUFFIXE_ELECTRIQUE)
                    } else {
                        isVAE = "false"
                    }
                }

                if (normalizeBoolean(isVAE)) tabGearType = tabGearType.filter(e => e !== MF_SUFFIXE_ELECTRIQUE)

                // Does gearType and brand already exist in our lists ?
                let gearTypeFind
                let brandFind

                if (getBike?.bike_description?.gear_type_id) gearTypeFind = bikeTypes.find(b => b.value === getBike?.bike_description?.gear_type_id) || bikeTypes[0]

                if (getBike?.bike_description?.brand) brandFind = bikeBrands.find(b => b.label.toLowerCase() === getBike?.bike_description?.brand.toLowerCase()) || bikeBrands[0]

                return {
                    isVAE: isVAE,
                    bikeType: (gearTypeFind && (gearTypeFind.value !== 999)) ? gearTypeFind.label : getBike?.bike_description?.gear_type,
                    bikeTypeId: gearTypeFind ? gearTypeFind.value : getBike?.bike_description?.gear_type_id,
                    bikeBrand: (brandFind && (brandFind.value !== 999)) ? brandFind.label : getBike?.bike_description?.brand,
                    bikeBrandId: brandFind ? brandFind.value : "",
                    ...(normalizeBoolean(isVAE) ? {
                        bikeEngineSerial: getBike?.bike_description?.engine_serial_number,
                        bikeBatterySerial: getBike?.bike_description?.battery_serial_number
                    } : {})
                }
            })()
        }

        const loadedBikeSoldInfos = {
            // status === 6 --> retour magasin
            storeId: (getBike.bike_status === BIKE_BACK_STORE) ? getBike.bike_owner_store_id : getBike.bike_last_registered_by?.store_id,
            ...((getBike.bike_status === 1 || isBO) ? { // sold ?
                accountActivated: getBike?.mail_verified,
                owner: {
                    mail: getBike?.bike_owner?.mail,
                    ...(getBike?.bike_owner?.gender ? { ownerGender: getBike.bike_owner.gender } : {}),
                    ...(getBike?.bike_owner?.last_name ? { ownerName: getBike.bike_owner.last_name } : {}),
                    ...(getBike?.bike_owner?.first_name ? { ownerFirstname: getBike.bike_owner.first_name } : {}),
                    ...(getBike?.bike_owner?.social_reason ? { ownerRaisonSociale: getBike.bike_owner.social_reason } : {}),
                    ...(getBike?.bike_owner?.phone ? { ownerPhonenumber: getBike.bike_owner.phone } : {}),
                }
            } : {}),
            ...((getBike.bike_status === 5) || (getBike.bike_status === 2) ? { // in sale by owner
                owner: {
                    mail: getBike?.bike_owner?.mail
                }
            } : {}),
        }

        // Set local states
        setBikeStatus(getBike.bike_status)
        setCurrentBike({
            ...loadedBikeBasis,
            ...loadedBikeDescription,
            ...loadedBikeSoldInfos
        })

        // Has the current store (user) already seen/used/sold this bike ?
        // retour magasin status = 6 et le magasin n'est pas celui qui a vendu le vélo : on affiche "ce vélo n'est pas disponible à la vente"
        // status !== 6 et le magasin n'est pas celui qui a vendu le vélo : on affiche les infos vélos avec mail caché (écran Louise)
        if (getBike.bike_status !== null) {
            const bikeStoreId = loadedBikeSoldInfos.storeId
            const storeStoreId = "ST-" + getEntityId()
            const oldStoreId = getOldStoreId()

            // Un vendeur MFC avec un gender Intersport doit pouvoir revendre un vélo MFC status 6 d'un autre vélociste
            const isMFCIntersportSeller = entity?.entity_data?.code_chaine === INTER_CHAIN
            const isMFCBike = getBike?.operator === MFC_OPERATOR

            // Implémentation de 2 fixes pour Arcade :
            // 1 - Si le vélo n'est pas volé, on regarde s'il provient du Wallet associé au magasin. Si c'est le cas il peut
            //     vendre ce vélo (Arcade livre parfois des vélos par flotte et ceux-ci sont donc gérés depuis le wallet).
            // 2 - Si le vélo est de retour en magasin (statut 6) et que son storeId est celui d'Arcade, alors le magasin
            //     peut vendre le vélo comme s'il lui appartenait
            // Attention Arcade a 2 storeId différents qu'il faut bien prendre en compte ARCADE_STOREID || ARCADE_STOREID_ST
            if(entity.entity_data?.wallet_id !== getBike.consumer_id) setIsWalletSeller(false)
            if (getBike.bike_status !== BIKE_STOLEN && entity.entity_data?.wallet_id === getBike.consumer_id) {
                setIsSeller(false)
                setIsWalletSeller(true)
            } else if (getBike.bike_status === BIKE_BACK_STORE && (bikeStoreId === ARCADE_STOREID || bikeStoreId === ARCADE_STOREID_ST || (isMFCIntersportSeller && isMFCBike))) {
                //@TODO - attente API Mounir qui va nous retourner is_arcade
                // pour l'instant on se base sur le store_id arcade
                setIsSeller(true)
            } else if (bikeStoreId !== storeStoreId && bikeStoreId !== oldStoreId) {
                setIsSeller(false)
            }
        }
    }


    React.useEffect(() => {
        initialBike && preparedBike(initialBike)
    },[initialBike]) // eslint-disable-line

    return (
        <BikeStoreContext.Provider value={
            {
                currentBike,
                setCurrentBike,
                initialBike,
                setInitialBike,
                bikeStatus,
                showOwner,
                setShowOwner,
                isSeller,
                isWalletSeller,
                fetchBike,
                resetBikeStoreInfo
            }}>
            {children}
        </BikeStoreContext.Provider>
    )
}

export { useBikeStore, BikeStoreProvider }